<template>
    <div class="flex flex-col">
        <!-- Search Bar -->
        <div class="relative px-2 sm:px-4 pt-2 sm:pt-4">
            <input type="text" :value="searchText" @input="onSearchInput"
                :placeholder="props.collectionName === 'messages' ? 'Search by name or email...' : 'Search by title...'"
                class="search-input w-full px-10 py-3 sm:py-2 text-base sm:text-sm min-h-[44px] touch-manipulation" data-perf-ignore="true" />
            <div class="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <Search width="18" />
            </div>
        </div>

        <!-- Controls Section -->
        <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center px-2 sm:px-4 py-3 sm:py-4 gap-3">
            <div class="flex items-center flex-wrap gap-2 sm:gap-4">
                <!-- Page Size -->
                <div class="flex items-center gap-2">
                    <label class="text-xs sm:text-sm text-gray-200 whitespace-nowrap">Page Size</label>
                    <select :value="pageSize" @change="onPageSizeChange"
                        class="px-2 sm:px-3 py-2 sm:py-1 bg-gray-700 border border-gray-600 rounded text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 min-h-[44px] sm:min-h-0 touch-manipulation">
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <slot name="pageControls"></slot>
            </div>
            <div class="flex justify-end">
                <slot name="actions"></slot>
            </div>
        </div>

        <!-- Table Section -->
        <div class="relative">
            <div v-if="isLoading" class="absolute inset-0 bg-gray-900/50 z-10 flex items-center justify-center">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
            <slot name="table"></slot>
        </div>

        <!-- Pagination -->
        <div class="px-4 mt-4">
            <SimplePagination v-if="!isLoading && totalItems > 0" v-model:currentPage="currentPageSync"
                :pageSize="Number(pageSize)" :totalItems="totalItems" />
        </div>
    </div>
</template>

<script setup>
import { computed, ref, watch, onUnmounted } from 'vue';
import { Search } from 'lucide-vue-next';
import SimplePagination from './SimplePagination.vue';
import { projectFirestore } from '../firebase/config';
import { collection, query, orderBy, where, startAfter, limit, getDocs, getCountFromServer, onSnapshot } from 'firebase/firestore';

const props = defineProps({
    searchText: { type: String, default: '' },
    searchPlaceholder: { type: String, default: 'Search...' },
    pageSize: { type: [Number, String], default: 10 },
    currentPage: { type: Number, default: 1 },
    collectionName: { type: String, required: true },
    propertyFilter: { type: String, default: '' },
});

const emit = defineEmits(['update:searchText', 'update:pageSize', 'update:currentPage', 'dataUpdate']);

const isLoading = ref(false);
const totalItems = ref(0);
const lastDoc = ref(null);
const unsubscribe = ref(null);
const searchTimeout = ref(null);

const currentPageSync = computed({
    get: () => props.currentPage,
    set: (value) => emit('update:currentPage', value),
});

const onSearchInput = (event) => {
    const value = event.target.value;
    if (searchTimeout.value) clearTimeout(searchTimeout.value);

    emit('update:searchText', value);
    searchTimeout.value = setTimeout(() => {
        emit('update:currentPage', 1);
        setupRealtimeListener(true);
    }, 300);
};

const onPageSizeChange = (event) => {
    emit('update:pageSize', Number(event.target.value));
    emit('update:currentPage', 1);
    setupRealtimeListener(true);
};

const getQueryConstraints = (resetPagination = false) => {
    const constraints = [];

    if (!props.collectionName || typeof props.collectionName !== 'string') {
        console.error('Invalid collectionName:', props.collectionName);
        return constraints;
    }

    if (props.searchText) {
        const searchLower = props.searchText;
        if (props.collectionName === 'services') {
            constraints.push(
                where('title', '>=', searchLower),
                where('title', '<=', searchLower + '\uf8ff'),
                orderBy('title')
            );
        } else if (props.collectionName === 'messages') {
            constraints.push(
                where('name', '>=', searchLower),
                where('name', '<=', searchLower + '\uf8ff'),
                orderBy('name')
            );
        } else if (props.collectionName === 'projects') {
            constraints.push(
                where('title', '>=', searchLower),
                where('title', '<=', searchLower + '\uf8ff'),
                orderBy('title')
            );
        } else if (props.collectionName === 'categories') {
            constraints.push(
                where('name', '>=', searchLower),
                where('name', '<=', searchLower + '\uf8ff'),
                orderBy('name')
            );
        }
    } else {
        constraints.push(orderBy('createdAt', 'desc')); // Default sorting by createdAt descending
    }

    if (!resetPagination && lastDoc.value && props.currentPage > 1) {
        constraints.push(startAfter(lastDoc.value));
    }

    constraints.push(limit(Number(props.pageSize)));

    return constraints;
};

const getCountQueryConstraints = () => {
    const constraints = [];

    if (!props.collectionName || typeof props.collectionName !== 'string') {
        console.error('Invalid collectionName for count:', props.collectionName);
        return constraints;
    }

    if (props.searchText) {
        const searchLower = props.searchText;
        if (props.collectionName === 'services') {
            constraints.push(
                where('title', '>=', searchLower),
                where('title', '<=', searchLower + '\uf8ff')
            );
        } else if (props.collectionName === 'messages') {
            constraints.push(
                where('name', '>=', searchLower),
                where('name', '<=', searchLower + '\uf8ff')
            );
        } else if (props.collectionName === 'projects') {
            constraints.push(
                where('title', '>=', searchLower),
                where('title', '<=', searchLower + '\uf8ff')
            );
        } else if (props.collectionName === 'categories') {
            constraints.push(
                where('name', '>=', searchLower),
                where('name', '<=', searchLower + '\uf8ff')
            );
        }
    }

    return constraints;
};

const setupRealtimeListener = async (resetPagination = false) => {
    try {
        isLoading.value = true;


        if (!projectFirestore) {
            throw new Error('Firestore not initialized');
        }

        if (typeof props.collectionName !== 'string') {
            throw new Error(`Invalid collectionName: ${props.collectionName}`);
        }

        if (unsubscribe.value) {
            unsubscribe.value();
            unsubscribe.value = null;
        }

        const collectionRef = collection(projectFirestore, props.collectionName);
        const constraints = getQueryConstraints(resetPagination);
        if (constraints.length === 0) {
            throw new Error('No valid query constraints');
        }


        const currentQuery = query(collectionRef, ...constraints);

        unsubscribe.value = onSnapshot(currentQuery, {
            next: async (snapshot) => {
                try {
                    let docs = [];
                    snapshot.forEach((doc) => {
                        const data = doc.data();
                        docs.push({ id: doc.id, ...data });
                    });

                    if (props.searchText && props.collectionName === 'messages' && docs.length === 0) {
                        const emailConstraints = [
                            where('email', '>=', props.searchText.toLowerCase()),
                            where('email', '<=', props.searchText.toLowerCase() + '\uf8ff'),
                            orderBy('email'),
                            limit(Number(props.pageSize)),
                        ];
                        const emailQuery = query(collectionRef, ...emailConstraints);
                        const emailSnapshot = await getDocs(emailQuery);
                        emailSnapshot.forEach((doc) => {
                            docs.push({ id: doc.id, ...doc.data() });
                        });
                    }

                    if (snapshot.docs.length > 0) {
                        lastDoc.value = snapshot.docs[snapshot.docs.length - 1];
                    } else {
                        lastDoc.value = null;
                    }

                    const countConstraints = getCountQueryConstraints();
                    const countQuery = query(collectionRef, ...countConstraints);
                    const countSnapshot = await getCountFromServer(countQuery);
                    totalItems.value = countSnapshot.data().count;

                    emit('dataUpdate', docs);
                } catch (error) {
                    console.error('Snapshot error:', error);
                    emit('dataUpdate', []);
                    alert(`Error loading ${props.collectionName}: ${error.message}`);
                } finally {
                    isLoading.value = false;
                }
            },
            error: (error) => {
                console.error('Listener error:', error);
                isLoading.value = false;
                emit('dataUpdate', []);
                alert(`Failed to load ${props.collectionName}: ${error.message}`);
            },
        });
    } catch (error) {
        console.error('Setup listener error:', error);
        isLoading.value = false;
        emit('dataUpdate', []);
        alert(`Failed to initialize ${props.collectionName} listener: ${error.message}`);
    }
};

watch(
    [() => props.currentPage, () => props.pageSize, () => props.searchText],
    () => {
        setupRealtimeListener(props.currentPage === 1);
    }
);

setupRealtimeListener(true);

onUnmounted(() => {
    if (unsubscribe.value) unsubscribe.value();
    if (searchTimeout.value) clearTimeout(searchTimeout.value);
});
</script>

<style scoped>
input,
select {
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.search-input {
    @apply w-full px-4 py-2 pl-10 text-gray-200 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors;
}
</style>